import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Btn } from "../ui/index.jsx";

const TOSS_CLIENT_KEY = "live_ck_DpexMgkW36xa44QjN91wrGbR5ozO";

export default function CheckoutModal({ plan, currentPlan, token, onClose, onSuccess }) {
  const [step, setStep] = useState("confirm");
  const [error, setError] = useState(null);
  const [tossReady, setTossReady] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly"); // Sprint 23: 월간/연간 토글

  // Sprint 23: 월간/연간 가격 구조 (Pro 숨김)
  const PLANS = {
    starter: {
      name: "Starter",
      monthly: 9900,
      annualTotal: 99000,
      annualMonthlyEq: 8250,
    },
    core: {
      name: "Core",
      monthly: 19900,
      annualTotal: 199000,
      annualMonthlyEq: 16583,
    },
    // pro는 UI에서 숨김 — 혹시 넘어와도 방어
  };

  const info = PLANS[plan];

  // 숨김 플랜(pro) 접근 방어
  if (!info) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 20, border: `1px solid ${T.border}`, maxWidth: 380, width: "100%", padding: "36px 28px", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 8 }}>준비 중인 플랜이에요</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>이 플랜은 곧 공개될 예정이에요.</div>
          <Btn variant="primary" onClick={onClose}>닫기</Btn>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (document.getElementById("toss-sdk")) { setTossReady(true); return; }
    const script = document.createElement("script");
    script.id = "toss-sdk";
    script.src = "https://js.tosspayments.com/v2/standard";
    script.onload = () => setTossReady(true);
    script.onerror = () => console.error("[TOSS] SDK 로드 실패");
    document.head.appendChild(script);
  }, []);

  // 현재 선택된 가격
  const currentPrice = billingCycle === "annual" ? info.annualTotal : info.monthly;
  const priceLabel = billingCycle === "annual" ? "/년" : "/월";

  const handlePayment = async () => {
    if (!tossReady || !window.TossPayments) {
      setError("결제 시스템을 불러오는 중이에요. 잠시 후 다시 시도해주세요.");
      setStep("error");
      return;
    }
    try {
      setStep("card_register");
      const tossPayments = window.TossPayments(TOSS_CLIENT_KEY);
      const customerKey = `pause_${btoa(token).slice(0, 20).replace(/[^a-zA-Z0-9]/g, "x")}`;
      const payment = tossPayments.payment({ customerKey });
      // billingCycle을 URL 파라미터로 전달 → App.jsx 콜백에서 subscribe 호출 시 사용
      await payment.requestBillingAuth({
        method: "CARD",
        successUrl: `${window.location.origin}/payment/billing-success?plan=${plan}&cycle=${billingCycle}`,
        failUrl: `${window.location.origin}/payment/billing-fail`,
      });
    } catch (err) {
      if (err.code === "PAY_PROCESS_CANCELED" || err.code === "USER_CANCEL") { setStep("confirm"); return; }
      console.error("[TOSS] 카드 등록 에러:", err);
      setError(err.message || "카드 등록 중 오류가 발생했어요");
      setStep("error");
    }
  };

  const handleTestActivate = async () => {
    setStep("processing");
    try {
      const result = await api("/plan", {
        method: "POST",
        body: JSON.stringify({
          plan,
          billing_cycle: billingCycle,
          payment_key: "test_pending",
          order_id: `phase_${plan}_${Date.now()}`,
        }),
      }, token);
      if (result?.plan) {
        setStep("done");
        setTimeout(() => onSuccess(result.plan), 1500);
      } else { throw new Error("플랜 변경 실패"); }
    } catch (err) { setError(err.message); setStep("error"); }
  };

  const isDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const isTossConfigured = TOSS_CLIENT_KEY && !TOSS_CLIENT_KEY.includes("PLACEHOLDER");

  // 월간/연간 토글 버튼 스타일
  const cycleBtnStyle = (active) => ({
    flex: 1,
    padding: "10px 12px",
    borderRadius: 8,
    border: active ? `1px solid ${T.accent}` : `1px solid ${T.border}`,
    background: active ? T.accent + "15" : "transparent",
    color: active ? T.accent : T.textDim,
    fontSize: 13,
    fontWeight: active ? 800 : 600,
    cursor: "pointer",
    position: "relative",
    transition: "all 0.15s ease",
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 20, border: `1px solid ${T.border}`, maxWidth: 420, width: "100%", padding: "36px 28px" }}>

        {step === "confirm" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💳</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>Pause {info.name}</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>구독을 시작할 준비가 되셨나요?</div>
            </div>

            {/* Sprint 23: 월간/연간 토글 */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, background: T.bg, padding: 4, borderRadius: 10 }}>
              <button type="button" style={cycleBtnStyle(billingCycle === "monthly")} onClick={() => setBillingCycle("monthly")}>
                월간 결제
              </button>
              <button type="button" style={cycleBtnStyle(billingCycle === "annual")} onClick={() => setBillingCycle("annual")}>
                연간 결제
                <span style={{ position: "absolute", top: -8, right: -4, background: T.green || "#1F6B5C", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 6 }}>
                  2개월 무료
                </span>
              </button>
            </div>

            {/* 가격 박스 (Sprint 26-1: VAT 별도 + 실결제 분리) */}
            <div style={{ background: T.bg, borderRadius: 12, padding: 20, marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 32, fontWeight: 900, color: T.accent }}>
                {currentPrice.toLocaleString()}원
                <span style={{ fontSize: 16, color: T.textMuted, fontWeight: 600 }}>{priceLabel}</span>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, fontWeight: 600 }}>
                VAT 별도
              </div>
              <div style={{
                marginTop: 8,
                paddingTop: 10,
                borderTop: `1px dashed ${T.border}`,
                fontSize: 14,
                color: T.text,
              }}>
                실결제 <span style={{ fontWeight: 800, color: T.accent }}>{Math.round(currentPrice * 1.1).toLocaleString()}원</span>
                <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 4 }}>(VAT 포함)</span>
              </div>
              {billingCycle === "annual" ? (
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8, lineHeight: 1.6 }}>
                  월 {info.annualMonthlyEq.toLocaleString()}원 꼴 · <span style={{ color: T.green || "#1F6B5C", fontWeight: 700 }}>1년 일시 결제</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>매월 자동 결제</div>
              )}
            </div>

            {/* 안내 박스 (결제 주기별 문구 분리) */}
            <div style={{ background: T.bg, borderRadius: 12, padding: 16, marginBottom: 20, fontSize: 12, color: T.textDim, lineHeight: 1.8 }}>
              <div style={{ fontWeight: 700, color: T.text, marginBottom: 6 }}>결제 안내</div>
              {billingCycle === "annual" ? (
                <>
                  <div>• 1년치를 한 번에 결제해요 (자동 갱신 없음)</div>
                  <div>• 만료 7일 전 이메일로 알려드려요</div>
                  <div>• 월간 대비 2개월치가 무료예요</div>
                </>
              ) : (
                <>
                  <div>• 카드 등록 후 매월 자동 결제돼요</div>
                  <div>• 언제든 해지할 수 있어요 (다음 갱신일에 중지)</div>
                  <div>• 첫 결제는 카드 등록 즉시 진행돼요</div>
                </>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {isTossConfigured ? (
                <Btn variant="primary" style={{ width: "100%", justifyContent: "center", padding: "14px" }} onClick={handlePayment}>
                  {tossReady ? "카드 등록 및 결제" : "결제 시스템 로딩 중..."}
                </Btn>
              ) : (
                <>
                  <div style={{ background: T.yellow + "10", borderRadius: 12, padding: 14, textAlign: "center", border: `1px solid ${T.yellow}30` }}>
                    <div style={{ fontSize: 12, color: T.yellow, fontWeight: 700 }}>결제 시스템 연동 준비 중</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>토스페이먼츠 연동 후 카드 결제가 가능해요</div>
                  </div>
                  {isDev && (
                    <Btn variant="primary" style={{ width: "100%", justifyContent: "center", padding: "14px", opacity: 0.7 }} onClick={handleTestActivate}>
                      [DEV] 테스트 활성화
                    </Btn>
                  )}
                </>
              )}
              <Btn variant="ghost" style={{ width: "100%", justifyContent: "center" }} onClick={onClose}>취소</Btn>
            </div>
          </>
        )}

        {step === "card_register" && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>💳</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>카드 등록 중...</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>토스페이먼츠 결제창으로 이동합니다</div>
          </div>
        )}

        {step === "processing" && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>결제 처리 중...</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>잠시만 기다려주세요</div>
          </div>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: T.green }}>구독 시작!</div>
            <div style={{ fontSize: 14, color: T.text, marginTop: 8 }}>Pause {info.name} 플랜이 활성화됐어요</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>잠시 후 대시보드로 이동합니다...</div>
          </div>
        )}

        {step === "error" && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.red }}>오류 발생</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 8 }}>{error || "다시 시도해주세요"}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
              <Btn variant="ghost" onClick={onClose}>닫기</Btn>
              <Btn variant="primary" onClick={() => { setStep("confirm"); setError(null); }}>다시 시도</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
